// Auth API calls — all authentication-related backend communication

import { apiClient } from '../../lib/api-client.js';
import type {
  AuthUser,
  LoginResponse,
  MfaChallengeResponse,
  RegisterRequest,
  RegisterResponse,
} from '@soeinscheiss/shared';

/** API wrapper for typed success responses */
interface ApiSuccess<T> {
  success: true;
  data: T;
}

// ── Login ──

/** Standard login (may return MFA challenge) */
export async function login(email: string, password: string) {
  return apiClient<ApiSuccess<LoginResponse | MfaChallengeResponse>>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    credentials: 'include', // Send/receive cookies (refresh token)
  });
}

/** Complete MFA login with TOTP code */
export async function verifyMfa(mfaChallengeToken: string, code: string) {
  return apiClient<ApiSuccess<LoginResponse>>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ mfaChallengeToken, code }),
    credentials: 'include',
  });
}

// ── Registration ──

/** Register new tenant admin — returns verification-required, no session */
export async function register(data: RegisterRequest) {
  return apiClient<ApiSuccess<RegisterResponse>>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
    credentials: 'include',
  });
}

/** Accept client invitation via token */
export async function acceptInvitation(
  token: string,
  data: { password: string; firstName: string; lastName: string; locale?: string },
) {
  return apiClient<ApiSuccess<LoginResponse>>('/auth/accept-invitation', {
    method: 'POST',
    body: JSON.stringify({ token, ...data }),
    credentials: 'include',
  });
}

// ── Token Refresh ──

/** Refresh access token using httpOnly refresh cookie */
export async function refreshToken() {
  return apiClient<ApiSuccess<LoginResponse>>('/auth/refresh', {
    method: 'POST',
    credentials: 'include', // Refresh token is in httpOnly cookie
  });
}

// ── Session ──

/** Get current user's profile (validates access token) */
export async function getMe() {
  return apiClient<ApiSuccess<{ user: AuthUser }>>('/auth/me', {
    credentials: 'include',
  });
}

/** Logout — invalidates session server-side */
export async function logout() {
  return apiClient<ApiSuccess<null>>('/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

// ── Password Reset ──

/** Request password reset email */
export async function forgotPassword(email: string) {
  return apiClient<ApiSuccess<{ message: string }>>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** Reset password with token from email */
export async function resetPassword(token: string, password: string) {
  return apiClient<ApiSuccess<{ message: string }>>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

// ── Magic Link ──

/** Request magic link login email (for clients) */
export async function requestMagicLink(email: string) {
  return apiClient<ApiSuccess<{ message: string }>>('/auth/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** Verify magic link token and create session */
export async function verifyMagicLink(token: string) {
  return apiClient<ApiSuccess<LoginResponse>>('/auth/magic-link/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
    credentials: 'include',
  });
}

// ── Email Verification ──

/** Verify email address with token */
export async function verifyEmail(token: string) {
  return apiClient<ApiSuccess<{ message: string }>>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/** Resend verification email */
export async function resendVerification(email: string) {
  return apiClient<ApiSuccess<{ message: string }>>('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// ── Passkey Auth (WebAuthn) ──

/** Start passkey authentication (get challenge options from server) */
export async function startPasskeyAuth(email?: string) {
  return apiClient<ApiSuccess<{ options: unknown; challenge: string }>>(
    '/auth/mfa/passkey/auth/start',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
      credentials: 'include',
    },
  );
}

/** Complete passkey authentication with browser credential response */
export async function completePasskeyAuth(response: unknown, challenge: string) {
  return apiClient<ApiSuccess<LoginResponse>>('/auth/mfa/passkey/auth/complete', {
    method: 'POST',
    body: JSON.stringify({ response, challenge }),
    credentials: 'include',
  });
}

// ── MFA Management ──

/** MFA status response shape */
export interface MfaStatus {
  totpEnabled: boolean;
  passkeyCount: number;
  passkeys: PasskeyInfo[];
  mfaEnabled: boolean;
}

/** Passkey info from backend */
export interface PasskeyInfo {
  id: string;
  credentialId: string;
  deviceType: string;
  backedUp: boolean;
  transports: string[];
  friendlyName: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

/** Get MFA status for current user */
export async function getMfaStatus() {
  return apiClient<ApiSuccess<MfaStatus>>('/auth/mfa/status');
}

/** Start TOTP setup — returns secret + otpauth URI for QR code */
export async function setupTotp() {
  return apiClient<ApiSuccess<{ secret: string; uri: string }>>('/auth/mfa/totp/setup', {
    method: 'POST',
  });
}

/** Confirm TOTP setup with first valid code — returns recovery codes */
export async function confirmTotp(code: string) {
  return apiClient<ApiSuccess<{ enabled: boolean; recoveryCodes: string[] }>>(
    '/auth/mfa/totp/confirm',
    {
      method: 'POST',
      body: JSON.stringify({ code }),
    },
  );
}

/** Disable TOTP MFA */
export async function disableTotp() {
  return apiClient<ApiSuccess<{ enabled: boolean }>>('/auth/mfa/totp', {
    method: 'DELETE',
  });
}

/** Start passkey registration (returns WebAuthn options) */
export async function startPasskeyRegistration() {
  return apiClient<ApiSuccess<unknown>>('/auth/mfa/passkey/register/start', {
    method: 'POST',
  });
}

/** Complete passkey registration with browser credential response */
export async function completePasskeyRegistration(response: unknown, friendlyName?: string) {
  return apiClient<ApiSuccess<unknown>>('/auth/mfa/passkey/register/complete', {
    method: 'POST',
    body: JSON.stringify({ response, friendlyName }),
  });
}

/** List user's registered passkeys */
export async function listPasskeys() {
  return apiClient<ApiSuccess<PasskeyInfo[]>>('/auth/mfa/passkeys');
}

/** Delete a specific passkey */
export async function deletePasskey(id: string) {
  return apiClient<ApiSuccess<{ deleted: boolean }>>(`/auth/mfa/passkeys/${id}`, {
    method: 'DELETE',
  });
}
